import {
  TDrawScheduleSelect,
  TDrawTypePlanSelect,
} from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import { TDrawTypeNumberSetup } from '@daymade/common-lib/dbJsonModels';
import { action, makeObservable, observable } from 'mobx';
import { drawApiClient, statsApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import { RootStore } from './RootStore';

export type DrawHydration = {
  drawTypePlans?: TDrawTypePlanSelect[];
  drawSchedules?: TDrawScheduleSelect[];
  upcomingDrawDate?: Date;
  treesPlantedCount?: number;
  prizesWonCount?: number;
  prizesWonValue?: number;
  numberSetup?: TDrawTypeNumberSetup;
};

const CHECK_DRAW_DATE_INTERVAL_MILLISECONDS = 5000;

export class DrawStore {
  root: RootStore;

  drawTypeCode = 'DAYMADEDRAW';

  loaded: boolean = false;
  // We must use an undefined values as the default, so that the date is parsed as NaN, because
  // new Date(null) actually returns the unix epoch
  upcomingDrawDate: Date | undefined = undefined;
  treesPlantedCount: number | undefined = 0;
  prizesWonCount: number | undefined = 0;
  prizesWonValue: number | undefined = 0;
  winnersLastWeek: number = 0;
  drawTypePlans: Record<string, Record<string, TDrawTypePlanSelect>> = {};
  drawSchedules: Record<string, TDrawScheduleSelect> = {};

  numberSetup: TDrawTypeNumberSetup = {
    type: GameType.LOTTERY,
    main: [55, 55, 55, 55],
    bonus: [10],
  };

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      loaded: observable,
      upcomingDrawDate: observable,
      treesPlantedCount: observable,
      prizesWonCount: observable,
      prizesWonValue: observable,
      winnersLastWeek: observable,
      numberSetup: observable,
      drawTypePlans: observable,
      drawSchedules: observable,
      drawTypeCode: observable,
      setNumberSetup: action,
      setDrawTypePlanSelects: action,
      setDrawSchedules: action,
      setUpcomingDrawDate: action,
      setHomePageStats: action,
      hydrate: action,
    });

    this.initAsync();
  }

  async initAsync() {
    await this.fetchDrawType();
    await this.fetchHomePageStats();

    this.loaded = true;

    // only run client side
    if (typeof window !== 'undefined') {
      // Get the initial upcoming draw date, but do it in a timeout so we don't cause any hydration errors
      setTimeout(
        () =>
          this.fetchDrawType().catch((e) => {
            console.warn('Failed to get initial upcoming draw date', e);
          }),
        1
      );
      // Use a setTimeout rather than a setInterval, as browsers will pause and then queue-up the
      // setInterval handler. This could result in multiple calls to fetchUpcomingDrawDate running
      // concurrently
      setTimeout(this.checkDrawDate, CHECK_DRAW_DATE_INTERVAL_MILLISECONDS);
    }
  }

  protected async fetchDrawType() {
    const resp = await drawApiClient.public.getDrawTypeCached({
      params: { draw_code: this.drawTypeCode },
    });

    if (resp.status == HttpStatusCode.OK) {
      if (resp.body.next_draw?.drawDate) {
        this.setUpcomingDrawDate(resp.body.next_draw?.drawDate);
      }

      if (resp.body.draw_type.numberSetup) {
        this.setNumberSetup(resp.body.draw_type.numberSetup);
      }

      this.setDrawTypePlanSelects(resp.body.draw_type_plans);
      this.setDrawSchedules(resp.body.draw_schedules);
    }
  }

  protected checkDrawDate = async () => {
    try {
      if (this.upcomingDrawDate && this.upcomingDrawDate < new Date()) {
        await this.fetchDrawType();
      }
    } finally {
      // Requeue the timeout once we're finished. If we used setInterval multiple calls
      // could be called queued by the browser then fired repeatedly
      setTimeout(this.checkDrawDate, CHECK_DRAW_DATE_INTERVAL_MILLISECONDS);
    }
  };

  protected async fetchHomePageStats() {
    const resp = await statsApiClient.public.getHomepageStats();

    if (resp.status === HttpStatusCode.OK) {
      const {
        treesPlantedCount,
        prizesWonCount,
        winnersLastWeek,
        prizesWonValue,
      } = resp.body;
      this.setHomePageStats(
        treesPlantedCount,
        prizesWonCount,
        winnersLastWeek,
        prizesWonValue
      );
    }
  }

  setDrawTypePlanSelects(drawTypePlans: TDrawTypePlanSelect[]) {
    for (const plan of drawTypePlans) {
      if (!this.drawTypePlans[plan.code]) {
        this.drawTypePlans[plan.code] = {};
      }
      this.drawTypePlans[plan.code][plan.subcode] = plan;
    }
  }

  setDrawSchedules(drawSchedules: TDrawScheduleSelect[]) {
    for (const schedule of drawSchedules) {
      this.drawSchedules[schedule.id] = schedule;
    }
  }

  setNumberSetup(numberSetup: TDrawTypeNumberSetup) {
    this.numberSetup = numberSetup;
  }

  setUpcomingDrawDate(upcomingDrawDate: Date) {
    this.upcomingDrawDate = upcomingDrawDate;
  }

  setHomePageStats(
    treesPlantedCount: number,
    prizesWonCount: number,
    winnersLastWeek: number,
    prizesWonValue: number
  ) {
    this.prizesWonCount = prizesWonCount;
    this.treesPlantedCount = treesPlantedCount;
    this.winnersLastWeek = winnersLastWeek;
    this.prizesWonValue = prizesWonValue;
  }

  hydrate(data?: DrawHydration) {
    if (data && data.upcomingDrawDate) {
      this.upcomingDrawDate = new Date(data.upcomingDrawDate);
    }
    if (data && data.treesPlantedCount) {
      this.treesPlantedCount = data.treesPlantedCount;
    }
    if (data && data.prizesWonCount) {
      this.prizesWonCount = data.prizesWonCount;
    }
    if (data && data.prizesWonValue) {
      this.prizesWonValue = data.prizesWonValue;
    }
    if (data && data.numberSetup) {
      this.numberSetup = data.numberSetup;
    }
    if (data && data.drawTypePlans) {
      this.setDrawTypePlanSelects(data.drawTypePlans);
    }

    if (data && data.drawSchedules) {
      this.setDrawSchedules(data.drawSchedules);
    }

    this.loaded = true;
  }
}
